import { Ticket, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/Button";
import { formatBookingDateWithWeekday } from "~/lib/utils";

interface CartItemProps {
  item: any;
  cmsService: any;
  serviceShortDescription?: string;
  quantity?: number;
  voucherCount?: number;
  totalPrice?: number;
  voucherCode?: string;
  voucherCodes?: string[];
  onRemove?: (item: any, isMainService: boolean) => void;
  isMainService?: boolean;
  loadingRemove?: boolean;
  hideRemoveButtons?: boolean;
  upsellingLoading?: boolean;
  showDate?: boolean;
  i18n: any;
}

export default function CartItem({
  item,
  cmsService,
  serviceShortDescription,
  quantity,
  voucherCount = 0,
  totalPrice,
  voucherCode: voucherCodeProp,
  voucherCodes: voucherCodesProp,
  onRemove,
  isMainService = false,
  loadingRemove = false,
  hideRemoveButtons = false,
  upsellingLoading = false,
  showDate = false,
  i18n,
}: CartItemProps) {
  const { t } = useTranslation();

  const handleDeleteClick = () => {
    if (upsellingLoading || !onRemove) return;
    onRemove(item, isMainService);
  };

  const price =
    totalPrice !== undefined ? totalPrice : item.GrossAmount?.Price || 0;
  const voucherCodesList = voucherCodesProp?.length
    ? voucherCodesProp
    : voucherCodeProp ?? (item as any).VoucherCode
    ? [voucherCodeProp ?? (item as any).VoucherCode]
    : [];
  const isIncluded = voucherCodesList.length > 0;

  const displayTitle = cmsService
    ? cmsService.acf.title
    : item.Service || (item as any).Service;

  const hasTime =
    item.StartTime &&
    !item.StartTime.startsWith("00:00:00") &&
    !item.EndTime?.startsWith("23:59:59");

  const hideEndTime = (item as any).HideEndTime || false;
  const timeDisplay = hasTime
    ? `${item.StartTime.slice(0, 5)}${
        !hideEndTime && item.EndTime && !item.EndTime.startsWith("23:59:59")
          ? ` - ${item.EndTime.slice(0, 5)}`
          : ""
      }`
    : null;

  const dateDisplay =
    showDate && item.CheckinDate
      ? formatBookingDateWithWeekday(item.CheckinDate, i18n.language)
      : null;

  return (
    <div className="flex flex-row justify-between border-b border-gray-200 pb-3 mt-3 first-of-type:mt-0">
      <div className="flex flex-col">
        <span className={`font-medium text-lg`}>
          {displayTitle}
          {quantity && quantity > 1 && (
            <span className="ml-1 text-sm text-secondary font-bold">
              x {quantity}
            </span>
          )}
        </span>
        <span className={"text-md text-gray-500"}>
          {serviceShortDescription}
          {hasTime && (
            <>
              <br />
              <span className="block text-black">{timeDisplay}</span>
            </>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {isIncluded ? (
          <div className="flex items-center gap-1 group relative">
            <span className="text-primary font-semibold whitespace-nowrap pl-2">
              {t("Incluso")}
            </span>
            <div className="relative">
              <Ticket
                size={16}
                className="text-green-600 cursor-help hover:text-gray-600 transition-colors mt-[1px]"
              />
              <div className="absolute bottom-full right-0 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 w-56 bg-gray-900 text-white text-xs rounded py-2 px-3 shadow-xl pointer-events-none">
                {voucherCodesList.length > 1
                  ? t("Incluso con i voucher") +
                    " " +
                    voucherCodesList.join(", ")
                  : t("Incluso con il voucher") + " " + voucherCodesList[0]}
                <div className="absolute top-full right-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1 group relative">
            <span className="font-semibold whitespace-nowrap pl-2">
              €{" "}
              {price.toLocaleString(i18n.language, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
            </span>
            {voucherCodesList.length > 0 && (
              <div className="relative flex items-center gap-1">
                <Ticket
                  size={16}
                  className="text-green-600 cursor-help hover:text-gray-600 transition-colors"
                />
                {voucherCount > 1 && (
                  <span className="text-xs text-gray-500">{voucherCount}</span>
                )}
                <div className="absolute bottom-full right-0 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 w-56 bg-gray-900 text-white text-xs rounded py-2 px-3 shadow-xl pointer-events-none">
                  {voucherCodesList.length > 1
                    ? t("Incluso con i voucher") +
                      " " +
                      voucherCodesList.join(", ")
                    : (voucherCount || 1) +
                      " " +
                      (voucherCount === 1 ? t("incluso") : t("inclusi")) +
                      " " +
                      t("con il voucher") +
                      " " +
                      voucherCodesList[0]}
                  <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            )}
          </div>
        )}
        {onRemove && !hideRemoveButtons && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={handleDeleteClick}
            disabled={loadingRemove || upsellingLoading}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
